import { collection, doc, getDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from './firebase';
import { Lab, UserProfile } from '../types/types';

const LABS_COLLECTION = 'labs';
const USERS_COLLECTION = 'users';

export const createLab = async (labData: Omit<Lab, 'id' | 'createdAt' | 'updatedAt' | 'ownerId'>, ownerUid: string): Promise<string> => {
    try {
        const batch = writeBatch(db);

        // 1. Create Lab Document
        const labRef = doc(collection(db, LABS_COLLECTION));
        const labId = labRef.id;

        const newLab: Lab = {
            id: labId,
            ...labData,
            ownerId: ownerUid,
            subscriptionStatus: 'trialing',
            subscriptionEndsAt: Timestamp.fromMillis(Date.now() + 15 * 24 * 60 * 60 * 1000), // 15 days trial
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };

        batch.set(labRef, newLab);

        // 2. Update User Profile to link to this Lab
        const userRef = doc(db, USERS_COLLECTION, ownerUid);
        // We assume the user profile might already exist, but if not, we should probably create it. 
        // However, for safety in this specific flow, we'll assume the user is logged in and has a profile 
        // (or we create one if we want to be robust, but let's stick to update for now as per plan).
        // Actually, let's use set with merge to be safe.

        const userUpdate: Partial<UserProfile> = {
            labId: labId,
            role: 'owner',
            updatedAt: Timestamp.now()
        };

        batch.set(userRef, userUpdate, { merge: true });

        await batch.commit();
        console.log('Lab created and user linked:', labId);
        return labId;

    } catch (error) {
        console.error('Error creating lab:', error);
        throw error;
    }
};

export const getLab = async (labId: string): Promise<Lab | null> => {
    try {
        const labRef = doc(db, LABS_COLLECTION, labId);
        const docSnap = await getDoc(labRef);

        if (docSnap.exists()) {
            return docSnap.data() as Lab;
        } else {
            console.log('No lab found for:', labId);
            return null;
        }
    } catch (error) {
        console.error('Error getting lab:', error);
        throw error;
    }
};

export const updateLab = async (labId: string, updates: Partial<Lab>): Promise<void> => {
    try {
        const labRef = doc(db, LABS_COLLECTION, labId);
        await import('firebase/firestore').then(({ updateDoc }) =>
            updateDoc(labRef, {
                ...updates,
                updatedAt: Timestamp.now()
            })
        );
        console.log('Lab updated:', labId);
    } catch (error) {
        console.error('Error updating lab:', error);
        throw error;
    }
};
